import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { ConvertedFile } from '@isophi/core-legacy';
import { getFileSizeString, ToastService } from '@isophi/core-ui';
import { TranslocoService } from '@jsverse/transloco';
import { BehaviorSubject } from 'rxjs';

export interface WrappedFile {
  file: File;
  id?: string;
}
export type WrappedFileType = 'image' | 'video' | 'document';

export interface WrapperRemoveFile {
  index: number;
  fileId: WrappedConvertedFileData['id'];
}

export type WrappedConvertedFile = ConvertedFile<WrappedConvertedFileData>;
export type WrappedConvertedFileData = { id: string; type: WrappedFileType };

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FileUploadComponent {
  private readonly translocoService = inject(TranslocoService);
  private readonly toastService = inject(ToastService);

  @Input() maxFilesCount = 4;
  /** Maximum file size in bytes. Default 10 MB */
  @Input() maxFilesSize = 10 * 1024 * 1024;
  @Input() files: WrappedConvertedFile[] | null = [];
  @Input() title = '';
  @Input() showCounter = false;
  @Input() accept: string;
  @Input() loading: boolean;

  @Output() filesToProccess = new EventEmitter<File[]>();

  @Output() removeFile = new EventEmitter<WrapperRemoveFile>();

  protected readonly isDragging$ = new BehaviorSubject(false);

  // File upload methods
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging$.next(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging$.next(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging$.next(false);

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) {
      return;
    }

    const filesThatCanBeAdded = this.filesThatCanBeAdded(files);
    if (filesThatCanBeAdded?.length > 0) {
      this.filesToProccess.emit(filesThatCanBeAdded);
    }
  }

  onFileSelected(event: Event): void {
    const target = event.target as HTMLInputElement;
    const files = target.files;

    if (!files || files.length === 0) {
      return;
    }

    const filesThatCanBeAdded = this.filesThatCanBeAdded(files);
    if (filesThatCanBeAdded?.length > 0) {
      this.filesToProccess.emit(filesThatCanBeAdded);
    }
  }

  private filesThatCanBeAdded(files: FileList): File[] {
    const filesThatCanBeAdded: File[] = [];

    for (const file of Array.from(files)) {
      const currentFilesCount = this.files.length + filesThatCanBeAdded.length;
      if (file.size > this.maxFilesSize) {
        this.toastService.error(
          this.translocoService.translate('shared/fileSizeError', { file: file.name, maxFileSize: getFileSizeString(this.maxFilesSize) })
        );
        continue;
      }
      if (currentFilesCount >= this.maxFilesCount) {
        let msg = 'shared/maxFileCount';
        if (this.maxFilesCount > 1) {
          msg = 'shared/maxFilesCount';
        }
        this.toastService.error(this.translocoService.translate(msg, { count: this.maxFilesCount }));
        return filesThatCanBeAdded;
      }

      filesThatCanBeAdded.push(file);
    }

    return filesThatCanBeAdded;
  }
}
